import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import nlCommon from './locales/nl/common.json';
import nlAuth from './locales/nl/auth.json';
import nlNav from './locales/nl/nav.json';
import nlStatistics from './locales/nl/statistics.json';
import nlPlayerView from './locales/nl/playerView.json';
import nlMatches from './locales/nl/matches.json';
import nlLineups from './locales/nl/lineups.json';
import nlPlayers from './locales/nl/players.json';
import nlFormations from './locales/nl/formations.json';
import nlAnalysis from './locales/nl/analysis.json';
import nlNotifications from './locales/nl/notifications.json';
import nlSettings from './locales/nl/settings.json';
import nlDashboard from './locales/nl/dashboard.json';
import nlDrills from './locales/nl/drills.json';
import nlLive from './locales/nl/live.json';
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enNav from './locales/en/nav.json';
import enStatistics from './locales/en/statistics.json';
import enPlayerView from './locales/en/playerView.json';
import enMatches from './locales/en/matches.json';
import enLineups from './locales/en/lineups.json';
import enPlayers from './locales/en/players.json';
import enFormations from './locales/en/formations.json';
import enAnalysis from './locales/en/analysis.json';
import enNotifications from './locales/en/notifications.json';
import enSettings from './locales/en/settings.json';
import enDashboard from './locales/en/dashboard.json';
import enDrills from './locales/en/drills.json';
import enLive from './locales/en/live.json';

/**
 * Nederlands is de vaste standaard én de fallback. De Engelse vertaling is bedoeld
 * voor Engelstaligen bínnen de Nederlandse markt (expat-ouders en -coaches), niet
 * voor uitbreiding naar het buitenland. Draai de standaard dus nooit om op basis
 * van browsertaal-statistieken.
 */
export const DEFAULT_LANGUAGE = 'nl';
export const SUPPORTED_LANGUAGES = ['nl', 'en'] as const;
export const LANGUAGE_STORAGE_KEY = 'hockey-app-language';

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Alle namespaces worden statisch geïmporteerd. Met twee talen is de payload klein
 * genoeg (~30 KB gzipped) dat een lazy-loading backend niet de moeite waard is.
 * Nieuwe namespace? Voeg hem hier in beide talen toe, dan pikt `npm run i18n:check`
 * het verschil op zodra een van de twee achterloopt.
 */
export const resources = {
  nl: {
    common: nlCommon,
    auth: nlAuth,
    nav: nlNav,
    statistics: nlStatistics,
    playerView: nlPlayerView,
    matches: nlMatches,
    lineups: nlLineups,
    players: nlPlayers,
    formations: nlFormations,
    analysis: nlAnalysis,
    notifications: nlNotifications,
    settings: nlSettings,
    dashboard: nlDashboard,
    drills: nlDrills,
    live: nlLive,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    nav: enNav,
    statistics: enStatistics,
    playerView: enPlayerView,
    matches: enMatches,
    lineups: enLineups,
    players: enPlayers,
    formations: enFormations,
    analysis: enAnalysis,
    notifications: enNotifications,
    settings: enSettings,
    dashboard: enDashboard,
    drills: enDrills,
    live: enLive,
  },
};

export const NAMESPACES = [
  'common',
  'auth',
  'nav',
  'statistics',
  'playerView',
  'matches',
  'lineups',
  'players',
  'formations',
  'analysis',
  'notifications',
  'settings',
  'dashboard',
  'drills',
  'live',
];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    // Zet 'en-US' en 'nl-NL' uit de browser om naar 'en' en 'nl'. Zonder dit valt
    // elke regiovariant terug op de fallback en zou een Engelse browser Nederlands krijgen.
    load: 'languageOnly',
    defaultNS: 'common',
    ns: NAMESPACES,
    detection: {
      // querystring staat vooraan zodat ?lng=en een scherm in één klik te controleren is.
      order: ['querystring', 'localStorage', 'navigator'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React escaped zelf al.
      escapeValue: false,
    },
    // Ontbrekende sleutels moeten tijdens de migratie opvallen, maar nooit in productie loggen.
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lngs, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] ontbrekende sleutel: ${ns}:${key} (${lngs.join(', ')})`);
      }
    },
  });

// Houdt <html lang> gelijk aan de actieve taal, voor screenreaders, spellingcontrole
// en automatische vertaling. Vuurt ook bij init, dus geen aparte aanroep nodig.
i18n.on('languageChanged', (lng) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
});

export default i18n;
